const STORAGE_KEY = '__MEDJ_BREADCRUMB_STACK__'

function safeParse(json, fallback) {
  try {
    return JSON.parse(json)
  } catch (e) {
    return fallback
  }
}

function normalizeFullPath(route) {
  return route?.fullPath || route?.path || ''
}

function toUpperText(text, fallback = 'DETAIL') {
  const val = String(text || '').trim()
  return (val || fallback).toUpperCase()
}

function truncateText(text, max = 25) {
  const val = String(text || '').trim()
  if (!val) return ''
  return val.length > max ? `${val.slice(0, max)}...` : val
}

function buildCollectionPath({ basePath, moduleTitle, module, collectionId, collectionTitle }) {
  const q = []
  if (collectionId) q.push(`id=${encodeURIComponent(String(collectionId))}`)
  if (collectionTitle) q.push(`title=${encodeURIComponent(String(collectionTitle))}`)
  if (moduleTitle) q.push(`moduleTitle=${encodeURIComponent(String(moduleTitle))}`)
  if (module) q.push(`module=${encodeURIComponent(String(module))}`)
  return q.length ? `${basePath}?${q.join('&')}` : basePath
}

function homeCrumb() {
  return { text: 'HOME', fullPath: '/' }
}

function cleanStack(list) {
  return (list || []).filter((x) => x && x.text && x.fullPath)
}

function inferArticleModule(route, prevStack = []) {
  const q = route?.query || {}
  const fromQuery = String(q.module || q.moduleTitle || '').trim()
  if (fromQuery) return fromQuery

  // 详情页无 module 时，用上一跳面包屑上下文推断
  const textSet = new Set((prevStack || []).map((x) => String(x?.text || '').toUpperCase()))
  if (textSet.has('INNOVATION TRACKER') || textSet.has('INNOVATION THERAPY')) return 'innovationTracker'
  if (textSet.has('DOCTORS')) return 'doctors'
  if (textSet.has('WEBINAR')) return 'webinar'
  if (textSet.has('CONFERENCES')) return 'conferences'
  if (textSet.has('LATEST NEWS')) return 'latestNews'
  if (textSet.has('FRONTIER RESEARCH')) return 'frontierResearch'
  if (textSet.has('DEEP ANALYSIS')) return 'deepAnalysis'
  return ''
}

function buildFixedStack(route, overrideText, prevStack = []) {
  const name = route?.name || ''
  const q = route?.query || {}
  const fullPath = normalizeFullPath(route)
  const root = homeCrumb()

  if (route?.path === '/oriental_express' || route?.path === '/' || name === 'home') {
    return [root]
  }

  if (name === 'innovationTrackerList') {
    return [
      root,
      { text: 'INNOVATION THERAPY', fullPath: '/innovationTracker/list?module=innovationTracker' },
      { text: 'INNOVATION TRACKER', fullPath: fullPath },
    ]
  }
  if (name === 'latestNewsList') {
    return [
      root,
      { text: 'INNOVATION THERAPY', fullPath: '/innovationTracker/list?module=innovationTracker' },
      { text: 'LATEST NEWS', fullPath: fullPath },
    ]
  }
  if (name === 'frontierResearchList') {
    return [
      root,
      { text: 'INNOVATION THERAPY', fullPath: '/innovationTracker/list?module=innovationTracker' },
      { text: 'FRONTIER RESEARCH', fullPath: fullPath },
    ]
  }
  if (name === 'deepAnalysisList') {
    return [
      root,
      { text: 'INNOVATION THERAPY', fullPath: '/innovationTracker/list?module=innovationTracker' },
      { text: 'INNOVATION TRACKER', fullPath: '/innovationTracker/list?module=innovationTracker' },
      { text: 'DEEP ANALYSIS', fullPath: fullPath },
    ]
  }

  if (name === 'doctorsList') {
    return [root, { text: 'DOCTORS', fullPath: fullPath }]
  }

  if (name === 'webinarContentAddCollection') {
    return [root, { text: 'WEBINAR', fullPath: fullPath }]
  }
  if (name === 'webinarContentLists') {
    return [
      root,
      { text: 'WEBINAR', fullPath: '/webinar/contentAddCollection/list?moduleTitle=webinar&module=webinar' },
      { text: 'CONTENT LISTS', fullPath: fullPath },
    ]
  }
  if (name === 'webinarCollectionLists') {
    return [
      root,
      { text: 'WEBINAR', fullPath: '/webinar/contentAddCollection/list?moduleTitle=webinar&module=webinar' },
      { text: toUpperText(overrideText || q.title || q.name, 'COLLECTION'), fullPath: fullPath },
    ]
  }

  if (name === 'contentList') {
    return [root, { text: 'CONFERENCES', fullPath: fullPath }]
  }
  if (name === 'contentLists') {
    return [
      root,
      { text: 'CONFERENCES', fullPath: '/conferences/contentAddCollection/list?moduleTitle=conferences&module=conferences' },
      { text: 'CONTENT LISTS', fullPath: fullPath },
    ]
  }
  if (name === 'conferencesCollectionLists') {
    return [
      root,
      { text: 'CONFERENCES', fullPath: '/conferences/contentAddCollection/list?moduleTitle=conferences&module=conferences' },
      { text: toUpperText(overrideText || q.title || q.name, 'COLLECTION'), fullPath: fullPath },
    ]
  }

  if (name === 'articleDetails') {
    const articleTitle = toUpperText(overrideText || 'ARTICLE DETAIL', 'ARTICLE DETAIL')
    const module = inferArticleModule(route, prevStack)
    const m = String(module).toLowerCase()
    const rawCollectionLabel = String(q.collectionTitle || q.title || q.name || '').trim()
    const collectionTitleFull = toUpperText(rawCollectionLabel, 'COLLECTION')
    const collectionTitle = toUpperText(truncateText(rawCollectionLabel, 25), 'COLLECTION')
    const webinarCollectionPath = buildCollectionPath({
      basePath: '/webinar/collectionLists/list',
      moduleTitle: 'webinar',
      module: 'webinarCollectionLists',
      collectionId: q.collectionId,
      collectionTitle: q.collectionTitle || q.title || q.name,
    })
    const conferencesCollectionPath = buildCollectionPath({
      basePath: '/conferences/collectionLists/list',
      moduleTitle: 'conferences',
      module: 'conferencesCollectionLists',
      collectionId: q.collectionId,
      collectionTitle: q.collectionTitle || q.title || q.name,
    })
    const nodes = [root]

    if (m.includes('innovation')) {
      nodes.push({ text: 'INNOVATION THERAPY', fullPath: '/innovationTracker/list?module=innovationTracker' })
      nodes.push({ text: 'INNOVATION TRACKER', fullPath: '/innovationTracker/list?module=innovationTracker' })
    } else if (m.includes('latestnews')) {
      nodes.push({ text: 'INNOVATION THERAPY', fullPath: '/innovationTracker/list?module=innovationTracker' })
      nodes.push({ text: 'LATEST NEWS', fullPath: '/latestNews/list?module=latestNews' })
    } else if (m.includes('frontierresearch')) {
      nodes.push({ text: 'INNOVATION THERAPY', fullPath: '/innovationTracker/list?module=innovationTracker' })
      nodes.push({ text: 'FRONTIER RESEARCH', fullPath: '/frontierResearch/list?module=frontierResearch' })
    } else if (m.includes('deepanalysis')) {
      nodes.push({ text: 'INNOVATION THERAPY', fullPath: '/innovationTracker/list?module=innovationTracker' })
      nodes.push({ text: 'INNOVATION TRACKER', fullPath: '/innovationTracker/list?module=innovationTracker' })
      nodes.push({ text: 'DEEP ANALYSIS', fullPath: '/deepAnalysis/list?module=deepAnalysis' })
    } else if (m.includes('doctor')) {
      nodes.push({ text: 'DOCTORS', fullPath: '/doctors/list?module=doctors' })
    } else if (m.includes('webinarcollectionlists')) {
      nodes.push({ text: 'WEBINAR', fullPath: '/webinar/contentAddCollection/list?moduleTitle=webinar&module=webinar' })
      nodes.push({
        text: collectionTitle,
        fullPath: webinarCollectionPath,
        ...(collectionTitle !== collectionTitleFull ? { titleFull: collectionTitleFull } : {}),
      })
    } else if (m.includes('conferencescollectionlists')) {
      nodes.push({ text: 'CONFERENCES', fullPath: '/conferences/contentAddCollection/list?moduleTitle=conferences&module=conferences' })
      nodes.push({
        text: collectionTitle,
        fullPath: conferencesCollectionPath,
        ...(collectionTitle !== collectionTitleFull ? { titleFull: collectionTitleFull } : {}),
      })
    } else if (m.includes('webinar')) {
      nodes.push({ text: 'WEBINAR', fullPath: '/webinar/contentAddCollection/list?moduleTitle=webinar&module=webinar' })
    } else if (m.includes('conference') || m.includes('contentlist')) {
      nodes.push({ text: 'CONFERENCES', fullPath: '/conferences/contentAddCollection/list?moduleTitle=conferences&module=conferences' })
    }
    nodes.push({ text: articleTitle, fullPath: fullPath || '/articleDetails' })
    return nodes
  }

  // 兜底：保留 HOME + 当前页，不再累加历史
  const title = toUpperText(q.moduleTitle || q.module || name || route?.path, 'DETAIL')
  return [root, { text: title, fullPath: fullPath || '/' }]
}

export function getBreadcrumbStack() {
  if (typeof window === 'undefined') return []
  const raw = window.sessionStorage.getItem(STORAGE_KEY)
  const list = safeParse(raw || '[]', [])
  return Array.isArray(list) ? list : []
}

export function setBreadcrumbStack(stack) {
  if (typeof window === 'undefined') return
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stack || []))
}

/**
 * 生成固定层级面包屑：
 * - 基于当前路由即时计算，不做历史叠加
 * - 详情页支持 overrideText 回写标题
 * - 搜索页不改写面包屑
 */
export function updateBreadcrumbStack(route, overrideText) {
  if (!route) return []

  // 搜索页不增加面包屑层级，也不覆盖已有上下文
  if (route?.name === 'Search' || route?.path === '/search') {
    return getBreadcrumbStack()
  }

  const prev = cleanStack(getBreadcrumbStack())
  const next = cleanStack(buildFixedStack(route, overrideText, prev))
  setBreadcrumbStack(next)
  return next
}

